import { useCallback, useEffect, useMemo, useSyncExternalStore } from "react";
import { ApiError, api } from "./client";
import { fetchClientConfig } from "./systemStatus";

export type NotificationStatus = "open" | "resolved" | "dismissed";
export type NotificationSeverity = "info" | "warning" | "critical" | string;

export type NotificationItem = {
  id: string;
  tenant_id: string;
  type: string;
  severity: NotificationSeverity;
  title: string;
  message: string | null;
  status: NotificationStatus;
  dedupe_key: string | null;
  created_at: string;
  resolved_at: string | null;
  read_at: string | null;
};

type ListResponse = {
  items: NotificationItem[];
  total: number;
  unread_count?: number;
};

export type NotificationsState = {
  open: NotificationItem[];
  unread: NotificationItem[];
  read: NotificationItem[];
  unreadCount: number;
  loading: boolean;
  error: string | null;
  reload: () => void;
  markAsRead: (id: string) => Promise<void>;
  markAsUnread: (id: string) => Promise<void>;
};

const DEFAULT_POLL_MS = 60_000;
const MIN_POLL_MS = 5_000;

/**
 * Paylasilan bildirim deposu: kac bilesen kullanirsa kullansin (zil + dashboard)
 * sunucuya TEK polling dongusu calisir. Aralik runtime_settings'ten gelir ve
 * Sistem Durumu ekranindan redeploy'suz degistirilebilir.
 */
type StoreSnapshot = {
  open: NotificationItem[];
  unreadCount: number;
  loading: boolean;
  error: string | null;
};

let snapshot: StoreSnapshot = { open: [], unreadCount: 0, loading: true, error: null };
const listeners = new Set<() => void>();
let activeConsumers = 0;
let pollTimer: ReturnType<typeof setInterval> | null = null;
let pollIntervalMs = DEFAULT_POLL_MS;
let intervalConfigRequested = false;
let fetchInFlight = false;

function emit(next: Partial<StoreSnapshot>): void {
  snapshot = { ...snapshot, ...next };
  listeners.forEach((listener) => listener());
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function getSnapshot(): StoreSnapshot {
  return snapshot;
}

async function fetchOpenNotifications(): Promise<void> {
  if (fetchInFlight) return;
  fetchInFlight = true;
  try {
    const res = await api.get<ListResponse>("/notifications", { status: "open", limit: 50 });
    const items = res.items || [];
    emit({
      open: items,
      unreadCount: res.unread_count ?? items.filter((i) => !i.read_at).length,
      loading: false,
      error: null,
    });
  } catch (err) {
    if (err instanceof ApiError && err.status === 403) {
      emit({ loading: false, error: null });
    } else if (err instanceof ApiError) {
      emit({ loading: false, error: err.message });
    } else {
      emit({ loading: false, error: "Bildirimler yüklenemedi." });
    }
  } finally {
    fetchInFlight = false;
  }
}

function startPolling(): void {
  if (pollTimer !== null) return;
  void fetchOpenNotifications();
  pollTimer = setInterval(() => void fetchOpenNotifications(), pollIntervalMs);

  if (!intervalConfigRequested) {
    intervalConfigRequested = true;
    fetchClientConfig()
      .then((cfg) => {
        const next = Number(cfg.notifications_poll_interval_ms);
        if (Number.isFinite(next) && next >= MIN_POLL_MS && next !== pollIntervalMs) {
          pollIntervalMs = next;
          if (pollTimer !== null) {
            clearInterval(pollTimer);
            pollTimer = setInterval(() => void fetchOpenNotifications(), pollIntervalMs);
          }
        }
      })
      .catch(() => undefined);
  }
}

function stopPolling(): void {
  if (pollTimer !== null) {
    clearInterval(pollTimer);
    pollTimer = null;
  }
}

function registerConsumer(): () => void {
  activeConsumers += 1;
  if (activeConsumers === 1) startPolling();
  return () => {
    activeConsumers -= 1;
    if (activeConsumers === 0) stopPolling();
  };
}

function splitByRead(items: NotificationItem[]): { unread: NotificationItem[]; read: NotificationItem[] } {
  const unread: NotificationItem[] = [];
  const read: NotificationItem[] = [];
  for (const item of items) {
    if (item.read_at) read.push(item);
    else unread.push(item);
  }
  return { unread, read };
}

export function useNotifications(enabled: boolean): NotificationsState {
  const state = useSyncExternalStore(subscribe, getSnapshot);

  useEffect(() => {
    if (!enabled) return undefined;
    return registerConsumer();
  }, [enabled]);

  const reload = useCallback(() => {
    void fetchOpenNotifications();
  }, []);

  const markAsRead = useCallback(async (id: string) => {
    const readAt = new Date().toISOString();
    emit({
      open: snapshot.open.map((item) => (item.id === id ? { ...item, read_at: readAt } : item)),
      unreadCount: Math.max(0, snapshot.unreadCount - 1),
    });
    try {
      await api.post(`/notifications/${id}/read`);
    } catch (err) {
      emit({ error: err instanceof ApiError ? err.message : "Bildirim okundu olarak işaretlenemedi." });
      void fetchOpenNotifications();
    }
  }, []);

  const markAsUnread = useCallback(async (id: string) => {
    emit({
      open: snapshot.open.map((item) => (item.id === id ? { ...item, read_at: null } : item)),
      unreadCount: snapshot.unreadCount + 1,
    });
    try {
      await api.post(`/notifications/${id}/unread`);
    } catch (err) {
      emit({ error: err instanceof ApiError ? err.message : "Bildirim okunmadı olarak işaretlenemedi." });
      void fetchOpenNotifications();
    }
  }, []);

  const { unread, read } = useMemo(() => splitByRead(state.open), [state.open]);

  return {
    open: state.open,
    unread,
    read,
    unreadCount: state.unreadCount,
    loading: enabled ? state.loading : false,
    error: state.error,
    reload,
    markAsRead,
    markAsUnread,
  };
}
